import React, { FC, useState, Fragment } from 'react';
import { Box, Typography, Chip, Divider, Button, Alert, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Steps } from 'models/workflow-step';
import { Order, OrderRejectStatus, OrderStatus } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { User } from 'models/user';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PauseResumeConfirmationDialog from 'components/lab-workflow/pause-resume-confirmation-dialog/pause-resume-confirmation-dialog.component';
import { ApolloCache, Reference, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import {
    changeWorkflowStepTemplate,
    completeWorkflowStepTemplate,
    pausedResumedStepTemplate,
    startWorkTemplate,
} from 'apollo/templates/workFlow';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { AccessTime } from '@mui/icons-material';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import SendToStepDialog from './send-to-step-dialog.component';
import { getNextProductionPlanStep } from 'utils/next-workflow-step';
import StartWorkDialog from './start-work-dialog.component';
import { pausedLabel } from 'utils/paused-label';
import { useNavigate } from 'react-router-dom';
import SubcontractTriggerButton, {
    opensSubcontractFlow,
    subcontractFlowPath,
} from './subcontract-trigger-button.component';

type WorkflowActionsProps = {
    order: Order;
    // Renders the actions as a single horizontal bar (status chip left, actions right)
    // instead of the default stacked/vertical layout. Used by the Plan Overview footer.
    horizontal?: boolean;
    // Signals that a workflow action went through, for a host that must react (a list that
    // drops the order, a dialog that closes). The order itself reaches every view through
    // the cache: each mutation here returns the Order (DD-3231).
    onCompleted?: () => void;
};

const WorkflowActions: FC<WorkflowActionsProps> = ({
    order,
    horizontal = false,
    onCompleted,
}: WorkflowActionsProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'workflow']);
    const currentOrg = currentOrgVar();
    const currentUser = currentUserVar();
    const [openPauseDialog, setOpenPauseDialog] = useState(false);
    const [isPauseConfirmLoading, setPauseConfirmLoading] = useState(false);
    const [showManualStepSelect, setShowManualStepSelect] = useState(false);
    const [stepToConfirm, setStepToConfirm] = useState<Steps | null>(null);
    const [openStartWorkConfirm, setOpenStartWorkConfirm] = useState(false);
    const [openCompleteStepsConfirm, setOpenCompleteStepsConfirm] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const navigate = useNavigate();

    const isProductionPlanningEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PRODUCTION_PLANNING,
    );

    // Derived from order + currentOrg — no state needed
    const availableSteps = (() => {
        if (!currentOrg.workFlow) return [];
        const stepsWithoutCurrent = currentOrg.workFlow.steps.filter(
            (s) => s._id !== order.workFlow?.currentStep?.stepId,
        );
        if (!permissions.isExternalTechnician(currentUser.role)) return stepsWithoutCurrent;
        return stepsWithoutCurrent.filter((step) => step.defaultExtUsers?.some((user) => user._id === currentUser._id));
    })();

    const showPauseResumeButton =
        !!order.workFlow?.currentStep?.stepId &&
        order.status.toUpperCase() === OrderStatus.ACCEPTED_BY_LAB &&
        order.rejectStatus?.toUpperCase() !== OrderRejectStatus.CANCELLED &&
        order.rejectStatus?.toUpperCase() !== OrderRejectStatus.DECLINED;

    const isPaused = order.workFlow?.currentStep?.isPaused ?? false;

    const showStartWorkButton = !!order.workFlow?.currentStep?.stepId && !order.workFlow.currentStep.workStarted;
    const isCurrentStepCompleted = order.workFlow?.currentStep?.name === 'ALL_STEPS_DONE';
    const nextPlanStep = getNextProductionPlanStep(order, isProductionPlanningEnabled);

    const nextWorkflowStep = nextPlanStep
        ? currentOrg.workFlow?.steps.find((s) => s._id === nextPlanStep.stepId)
        : null;
    const hasProductionPlanNextStep = !!nextWorkflowStep;

    const handleConfirmStep = async (userId: string, comment: string, deadline: Date | null) => {
        if (!stepToConfirm) return;
        const defaultExtUsers = stepToConfirm.defaultExtUsers?.map((user) => (user as User)._id);
        const movedToStep = stepToConfirm;
        const result = await changeWorkflowStep({
            variables: {
                orderCurrentStep: {
                    orderId: order?._id,
                    stepId: stepToConfirm._id,
                    name: stepToConfirm.name,
                    description: stepToConfirm.description,
                    comment,
                    maxHourInStep: stepToConfirm.maxHourInStep,
                    userId,
                    extUsers: defaultExtUsers,
                    millingDeadline: deadline,
                    useMillingDeadline: stepToConfirm.useMillingDeadline,
                },
            },
        });
        // DD-3488: moving this one order into a subcontract step opens the wizard for it.
        if (result.data && opensSubcontractFlow(movedToStep)) {
            navigate(subcontractFlowPath(order._id));
        }
    };

    const [changeWorkflowStep] = useMutation(changeWorkflowStepTemplate, {
        onCompleted: () => {
            setStepToConfirm(null);
            setShowManualStepSelect(false);
            onCompleted?.();
        },
        onError: (error) => showBoundary(error),
        update(cache: ApolloCache<Order[]>, { data: { changeWorkflowStep } }) {
            cache.modify({
                fields: {
                    stepOrders(existingStepOrderRefs: Reference | (Reference | undefined)[], { readField }) {
                        if (existingStepOrderRefs instanceof Array) {
                            return [...existingStepOrderRefs].filter((stepOrderRef: Reference | undefined) => {
                                return order?._id !== readField('_id', stepOrderRef);
                            });
                        }
                        return [];
                    },
                    orderWorkflow() {
                        return changeWorkflowStep.workFlow.stepLogs;
                    },
                },
            });
        },
    });

    const [pausedResumedStep] = useMutation(pausedResumedStepTemplate, {
        onCompleted: () => onCompleted?.(),
        onError: (error) => showBoundary(error),
    });

    const [startWork] = useMutation(startWorkTemplate, {
        onCompleted: () => onCompleted?.(),
        onError: (error) => showBoundary(error),
    });

    const handleStartWork = (userId: string) => {
        startWork({ variables: { orderId: order._id, userId } });
        setOpenStartWorkConfirm(false);
    };

    const [completeWorkflowStep] = useMutation(completeWorkflowStepTemplate, {
        onCompleted: () => onCompleted?.(),
        onError: (error) => showBoundary(error),
    });

    const handleCompleteAllSteps = (userId: string) => {
        completeWorkflowStep({ variables: { orderId: order._id, userId } });
        setOpenCompleteStepsConfirm(false);
    };

    const handlePauseConfirmOk = async (userId: string, comment: string, reason?: string) => {
        setPauseConfirmLoading(true);
        await pausedResumedStep({ variables: { orderId: order._id, userId, comment, reason } });
        setOpenPauseDialog(false);
        setPauseConfirmLoading(false);
    };

    const subDialogs = (
        <Fragment>
            <PauseResumeConfirmationDialog
                isPaused={isPaused}
                initialUserId={order.workFlow?.currentStep?.userId || currentUser._id}
                open={openPauseDialog}
                handleClose={() => setOpenPauseDialog(false)}
                handleOk={handlePauseConfirmOk}
                isLoading={isPauseConfirmLoading}
            />

            {stepToConfirm ? (
                <SendToStepDialog
                    step={stepToConfirm}
                    currentStepName={order.workFlow?.currentStep?.name}
                    currentStepUserId={order.workFlow?.currentStep?.userId}
                    onConfirm={handleConfirmStep}
                    onClose={() => setStepToConfirm(null)}
                />
            ) : undefined}

            {openStartWorkConfirm ? (
                <StartWorkDialog
                    open={openStartWorkConfirm}
                    stepName={order.workFlow?.currentStep?.name}
                    onConfirm={handleStartWork}
                    onClose={() => setOpenStartWorkConfirm(false)}
                />
            ) : undefined}

            {openCompleteStepsConfirm ? (
                <StartWorkDialog
                    open={openCompleteStepsConfirm}
                    title={translate('workflow:ALL_STEPS_DONE')}
                    confirmLabel={translate('workflow:ALL_STEPS_DONE')}
                    onConfirm={handleCompleteAllSteps}
                    onClose={() => setOpenCompleteStepsConfirm(false)}
                />
            ) : undefined}
        </Fragment>
    );

    if (horizontal) {
        return (
            <Fragment>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Line 1: current step, technician */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                        {order?.workFlow?.currentStep ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle2">{translate('orders:CURRENT_STEP')}:</Typography>
                                <Chip
                                    icon={
                                        !isCurrentStepCompleted && !order.workFlow.currentStep.workStarted ? (
                                            <AccessTime />
                                        ) : undefined
                                    }
                                    label={
                                        order.workFlow.currentStep.name === 'NO_STEP'
                                            ? translate('workflow:NO_STEP')
                                            : order.workFlow.currentStep.name === 'ALL_STEPS_DONE'
                                              ? translate('workflow:ALL_STEPS_DONE')
                                              : order.workFlow.currentStep.name
                                    }
                                    color={
                                        isCurrentStepCompleted
                                            ? 'success'
                                            : order.workFlow.currentStep.workStarted &&
                                                !order.workFlow.currentStep.isPaused
                                              ? 'primary'
                                              : 'warning'
                                    }
                                />
                                {!isCurrentStepCompleted && !order.workFlow.currentStep.workStarted ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('workflow:WAITING')}
                                    </Typography>
                                ) : undefined}
                                {order.workFlow.currentStep.user?.fullName ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {order.workFlow.currentStep.user.fullName}
                                    </Typography>
                                ) : undefined}
                            </Box>
                        ) : undefined}
                    </Box>

                    {/* Line 2: start work, move buttons, step select, pause */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                        <SubcontractTriggerButton order={order} />
                        {isPaused ? (
                            <Alert severity="warning" sx={{ py: 0 }} data-test="work-flow-actions.paused-alert">
                                {pausedLabel(order.workFlow?.currentStep?.pauseReason, translate)}
                            </Alert>
                        ) : (
                            <>
                                {showStartWorkButton && (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={() => setOpenStartWorkConfirm(true)}
                                        data-test="work-flow-actions.start-work-btn"
                                    >
                                        {translate('workflow:START_WORK')}
                                    </Button>
                                )}
                                {hasProductionPlanNextStep && (
                                    <>
                                        <Button
                                            size="small"
                                            variant={
                                                order.workFlow?.currentStep?.workStarted ? 'contained' : 'outlined'
                                            }
                                            onClick={() => setStepToConfirm(nextWorkflowStep!)}
                                            data-test="work-flow-actions.next-step-btn"
                                        >
                                            {translate('workflow:MOVE_TO')}: {nextPlanStep?.stepName}
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => setShowManualStepSelect((prev) => !prev)}
                                            data-test="work-flow-actions.move-to-another-step-btn"
                                        >
                                            {translate('workflow:MOVE_TO_ANOTHER_STEP')}
                                        </Button>
                                    </>
                                )}

                                {(!hasProductionPlanNextStep || showManualStepSelect) && (
                                    <>
                                        {!hasProductionPlanNextStep &&
                                            !isCurrentStepCompleted &&
                                            order.workFlow.currentStep.workStarted && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => setOpenCompleteStepsConfirm(true)}
                                                    data-test="work-flow-actions.all-steps-done-btn"
                                                >
                                                    {translate('workflow:ALL_STEPS_DONE')}
                                                </Button>
                                            )}
                                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                                            <Typography variant="subtitle2">
                                                {translate('orders:SELECT_NEW_WORKFLOW_STEP')}:
                                            </Typography>
                                            {order?.workFlow?.currentStep?.stepId ? (
                                                <Chip
                                                    clickable
                                                    label={translate('workflow:NO_STEP')}
                                                    onClick={() => setStepToConfirm({} as Steps)}
                                                    data-test="work-flow-actions.no-step-chip"
                                                />
                                            ) : undefined}
                                            {availableSteps.map((step, index) => (
                                                <Tooltip key={index} title={step.description || ''} arrow>
                                                    <Chip
                                                        clickable
                                                        label={step.name}
                                                        onClick={() => setStepToConfirm(step)}
                                                        data-test={`work-flow-actions.step-chip-${step.name}`}
                                                    />
                                                </Tooltip>
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </>
                        )}

                        {showPauseResumeButton && (
                            <Button
                                size="small"
                                onClick={() => setOpenPauseDialog(true)}
                                startIcon={isPaused ? <PlayCircleIcon /> : <PauseCircleIcon />}
                                data-test={
                                    isPaused
                                        ? 'work-flow-actions.un-pause-step-btn'
                                        : 'work-flow-actions.pause-step-btn'
                                }
                            >
                                {isPaused ? translate('workflow:UN_PAUSE_STEP') : translate('workflow:PAUSE_STEP')}
                            </Button>
                        )}
                    </Box>
                </Box>
                {subDialogs}
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Box>
                {order?.workFlow?.currentStep ? (
                    <Fragment>
                        <Box mb={2}>
                            <Typography variant="subtitle2" mb={1}>
                                {translate('orders:CURRENT_STEP')}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                    icon={
                                        !isCurrentStepCompleted && !order.workFlow.currentStep.workStarted ? (
                                            <AccessTime />
                                        ) : undefined
                                    }
                                    size="medium"
                                    label={
                                        order.workFlow.currentStep.name === 'NO_STEP'
                                            ? translate('workflow:NO_STEP')
                                            : order.workFlow.currentStep.name === 'ALL_STEPS_DONE'
                                              ? translate('workflow:ALL_STEPS_DONE')
                                              : order.workFlow.currentStep.name
                                    }
                                    color={
                                        isCurrentStepCompleted
                                            ? 'success'
                                            : order.workFlow.currentStep.workStarted &&
                                                !order.workFlow.currentStep.isPaused
                                              ? 'primary'
                                              : 'warning'
                                    }
                                />
                                {!isCurrentStepCompleted && !order.workFlow.currentStep.workStarted ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('workflow:WAITING')}
                                    </Typography>
                                ) : undefined}
                                {order.workFlow.currentStep.user?.fullName ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {order.workFlow.currentStep.user.fullName}
                                    </Typography>
                                ) : undefined}
                            </Box>
                        </Box>
                        <Box my={1}>
                            <Divider />
                        </Box>
                    </Fragment>
                ) : undefined}

                <SubcontractTriggerButton order={order} fullWidth />
                {isPaused ? (
                    <Box mb={1}>
                        <Alert severity="warning" data-test="work-flow-actions.paused-alert">
                            {pausedLabel(order.workFlow?.currentStep?.pauseReason, translate)}
                        </Alert>
                    </Box>
                ) : (
                    <>
                        {showStartWorkButton && (
                            <Box mb={1}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="success"
                                    onClick={() => setOpenStartWorkConfirm(true)}
                                    data-test="work-flow-actions.start-work-btn"
                                >
                                    {translate('workflow:START_WORK')}
                                </Button>
                            </Box>
                        )}

                        {hasProductionPlanNextStep && (
                            <>
                                <Box mb={1}>
                                    <Button
                                        fullWidth
                                        variant={order.workFlow?.currentStep?.workStarted ? 'contained' : 'text'}
                                        size={order.workFlow?.currentStep?.workStarted ? 'medium' : 'small'}
                                        onClick={() => setStepToConfirm(nextWorkflowStep!)}
                                        data-test="work-flow-actions.next-step-btn"
                                    >
                                        {translate('workflow:MOVE_TO')}: {nextPlanStep?.stepName}
                                    </Button>
                                </Box>
                                <Button
                                    fullWidth
                                    size="small"
                                    onClick={() => setShowManualStepSelect((prev) => !prev)}
                                    data-test="work-flow-actions.move-to-another-step-btn"
                                >
                                    {translate('workflow:MOVE_TO_ANOTHER_STEP')}
                                </Button>
                            </>
                        )}

                        {(!hasProductionPlanNextStep || showManualStepSelect) && (
                            <>
                                {!hasProductionPlanNextStep &&
                                    !isCurrentStepCompleted &&
                                    order.workFlow.currentStep.workStarted && (
                                        <Box mb={1}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="success"
                                                onClick={() => setOpenCompleteStepsConfirm(true)}
                                                data-test="work-flow-actions.all-steps-done-btn"
                                            >
                                                {translate('workflow:ALL_STEPS_DONE')}
                                            </Button>
                                        </Box>
                                    )}
                                <Typography variant="subtitle2" mb={1}>
                                    {translate('orders:SELECT_NEW_WORKFLOW_STEP')}
                                </Typography>
                                <Box mb={2} display="flex" flexWrap="wrap" sx={{ gap: 1 }}>
                                    {order?.workFlow?.currentStep?.stepId ? (
                                        <Chip
                                            size="medium"
                                            clickable
                                            label={translate('workflow:NO_STEP')}
                                            onClick={() => setStepToConfirm({} as Steps)}
                                            data-test="work-flow-actions.no-step-chip"
                                        />
                                    ) : undefined}
                                    {availableSteps.map((step, index) => (
                                        <Tooltip key={index} title={step.description || ''} arrow>
                                            <Chip
                                                size="medium"
                                                clickable
                                                label={step.name}
                                                onClick={() => setStepToConfirm(step)}
                                                data-test={`work-flow-actions.step-chip-${step.name}`}
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            </>
                        )}
                    </>
                )}

                {showPauseResumeButton && (
                    <Fragment>
                        <Box my={1}>
                            <Divider />
                        </Box>
                        <Button
                            fullWidth
                            onClick={() => setOpenPauseDialog(true)}
                            startIcon={isPaused ? <PlayCircleIcon /> : <PauseCircleIcon />}
                            data-test={
                                isPaused ? 'work-flow-actions.un-pause-step-btn' : 'work-flow-actions.pause-step-btn'
                            }
                        >
                            {isPaused ? translate('workflow:UN_PAUSE_STEP') : translate('workflow:PAUSE_STEP')}
                        </Button>
                    </Fragment>
                )}
            </Box>

            {subDialogs}
        </Fragment>
    );
};

export default WorkflowActions;
